export const legalTranslationsEn = {
  legal: {
    common: {
      backToHome: 'Back to home',
      lastUpdated: 'Last updated: September 2026',
    },
    footer: {
      privacy: 'Privacy Policy',
      cookies: 'Cookie Policy',
      terms: 'Terms of Service',
      cookieSettings: 'Cookie settings',
    },
    consent: {
      bannerTitle: 'We value your privacy',
      bannerDescription: 'Salovare uses necessary storage to keep the app secure and working. With your permission, we also store preferences, load third-party sign-in, and measure advertising performance.',
      acceptAll: 'Accept all',
      rejectNonEssential: 'Reject non-essential',
      customize: 'Customize',
      preferencesTitle: 'Cookie preferences',
      preferencesDescription: 'Choose which optional storage and integrations you allow. Necessary storage is always active because the service cannot work without it.',
      necessaryTitle: 'Necessary',
      necessaryDescription: 'Required for authentication, security, and core booking features.',
      functionalTitle: 'Functional',
      functionalDescription: 'Remembers language, form drafts, and dismissed notices to improve your experience.',
      thirdPartyTitle: 'Third-party',
      thirdPartyDescription: 'Loads Google Sign-In when you choose to authenticate with Google.',
      advertisingTitle: 'Advertising',
      advertisingDescription: 'Allows Google Ads to measure visits and conversions from our ads, using Google advertising cookies.',
      savePreferences: 'Save preferences',
      alwaysOn: 'Always on',
      googleDisabled: 'Google Sign-In is disabled until you allow third-party cookies in cookie settings.',
      openCookieSettings: 'Open cookie settings',
    },
    registration: {
      acceptLabel: 'I agree to the Terms of Service and Privacy Policy',
      acceptLabelRich: 'I agree to the <termsLink>Terms of Service</termsLink> and the <privacyLink>Privacy Policy</privacyLink>.',
      termsRequired: 'You must accept the Terms of Service and Privacy Policy to continue.',
    },
    privacy: {
      title: 'Privacy Policy',
      seoDescription: 'How Salovare processes personal data for barbershop and salon software, including store owners, team members, and customers.',
      intro: 'This Privacy Policy explains how {{productName}} processes personal data when you use our platform. {{productName}} is operated by {{controllerName}} (NIF {{nif}}), based in {{country}}.',
      contact: 'Privacy contact: {{privacyEmail}}',
      sections: [
        {
          title: 'Who is responsible for your data',
          paragraphs: [
            'Data controller: {{controllerName}} (NIF {{nif}}), {{country}}.',
            'Contact for privacy requests: {{privacyEmail}}.',
            'Salovare provides software for barber shops and salons. Store owners may act as separate controllers for their own customer data collected through their storefront.',
          ],
        },
        {
          title: 'Data we process',
          paragraphs: [
            'Account data: name, email, phone, authentication credentials, role, and preferences.',
            'Store and team data: business details, services, schedules, team members, and store settings.',
            'Customer booking data: appointments, selected services, membership information, and communication preferences.',
            'Payment-related data: payment status, billing references, and Stripe identifiers. Card details are processed by Stripe, not stored by Salovare.',
            'Technical data: IP address, browser type, device information, and security logs needed to protect the service.',
            'Advertising measurement data: ad click identifiers and conversion events, processed by Google Ads only if you consent to advertising cookies.',
          ],
        },
        {
          title: 'Why we process data',
          paragraphs: [
            'To provide and secure the Salovare platform (contract and legitimate interest).',
            'To manage appointments, memberships, and store operations requested by users and store staff.',
            'To process payments through Stripe when online payments are enabled.',
            'To authenticate users, including optional Google Sign-In when you choose it.',
            'To measure advertising performance with Google Ads when you consent to advertising cookies.',
            'To comply with legal obligations and respond to lawful requests.',
          ],
        },
        {
          title: 'Legal bases',
          paragraphs: [
            'Contract: providing the service you sign up for.',
            'Legitimate interests: securing the platform, preventing abuse, and improving reliability.',
            'Consent: optional functional storage, third-party sign-in, and advertising measurement where required.',
            'Legal obligation: tax, accounting, or regulatory requirements where applicable.',
          ],
        },
        {
          title: 'Sharing and subprocessors',
          paragraphs: [
            'We use trusted providers to operate Salovare, including hosting, email delivery, Stripe for payments, Google for optional authentication, and Google Ads for advertising measurement when you consent.',
            'Store customer data may be visible to authorized staff of the relevant store. Store owners are responsible for their own customer-facing privacy notices.',
            'We do not sell personal data.',
          ],
        },
        {
          title: 'International transfers',
          paragraphs: [
            'Some subprocessors may process data outside the European Economic Area. Where this happens, we rely on appropriate safeguards such as Standard Contractual Clauses or equivalent mechanisms provided by the vendor.',
          ],
        },
        {
          title: 'Retention',
          paragraphs: [
            'We keep personal data only for as long as needed to provide the service, meet legal obligations, resolve disputes, and enforce agreements.',
            'Retention periods may vary by data type and store activity. Contact us if you need details about a specific category.',
          ],
        },
        {
          title: 'Your rights',
          paragraphs: [
            'If you are in the EEA or UK, you may have the right to access, rectify, erase, restrict, or object to processing, and to data portability where applicable.',
            'You may withdraw consent at any time for processing based on consent, without affecting prior lawful processing.',
            'You may lodge a complaint with your local supervisory authority. In Portugal, this is the CNPD.',
            'To exercise your rights, email {{privacyEmail}}.',
          ],
        },
        {
          title: 'Security',
          paragraphs: [
            'We apply technical and organizational measures designed to protect personal data, including access controls, encrypted transport, and monitoring for abuse.',
          ],
        },
        {
          title: 'Changes',
          paragraphs: [
            'We may update this policy when our practices change. Material updates will be reflected on this page with a revised date.',
          ],
        },
      ],
    },
    cookies: {
      title: 'Cookie Policy',
      seoDescription: 'How Salovare uses cookies and browser storage for barbershop and salon software, including necessary, functional, sign-in, and optional advertising cookies.',
      intro: 'This Cookie Policy explains how {{productName}}, operated by {{controllerName}}, uses cookies and similar browser storage.',
      sections: [
        {
          title: 'What are cookies and similar technologies',
          paragraphs: [
            'Cookies are small text files stored on your device. We also use local storage for some preferences and session data.',
          ],
        },
        {
          title: 'Necessary storage',
          paragraphs: [
            'Authentication tokens and session identifiers required to keep you signed in securely.',
            'Security-related cookies used during authentication flows, including refresh token cookies set by our API.',
            'These are required to provide the service and cannot be disabled from the preference center.',
          ],
        },
        {
          title: 'Functional storage (optional)',
          paragraphs: [
            'preferred-language: remembers your language choice.',
            'Registration and update form drafts: temporary local saves so you do not lose progress.',
            'billing-reminder-dismissed:*: remembers dismissed billing reminders for store owners.',
          ],
        },
        {
          title: 'Third-party services (optional)',
          paragraphs: [
            'Google Sign-In (accounts.google.com): loaded only if you allow third-party cookies. Google may set its own cookies according to Google policies.',
            'Google Ads (googletagmanager.com / googleadservices.com): loaded to measure ad visits and conversions only if you allow advertising cookies. Google may set advertising cookies according to Google policies.',
            'Stripe: when you are redirected to Stripe-hosted payment or onboarding pages, Stripe may use cookies on stripe.com domains.',
          ],
        },
        {
          title: 'Managing your choices',
          paragraphs: [
            'Use the cookie banner or the "Cookie settings" link in the footer to update your preferences at any time.',
            'You can also control cookies through your browser settings. Blocking necessary storage may prevent the app from working correctly.',
          ],
        },
        {
          title: 'Contact',
          paragraphs: [
            'Questions about this policy: {{privacyEmail}}.',
          ],
        },
      ],
    },
    terms: {
      title: 'Terms of Service',
      seoDescription: 'Terms for using Salovare barbershop and salon software, including subscriptions, trials, stores, and acceptable use.',
      intro: 'These Terms govern your use of {{productName}}, operated by {{controllerName}}. By creating an account or using Salovare, you agree to these Terms.',
      sections: [
        {
          title: 'The service',
          paragraphs: [
            'Salovare provides online tools for appointment scheduling, store management, team management, memberships, and related features for barber shops and salons.',
            'We may update, suspend, or discontinue features with reasonable notice when possible.',
          ],
        },
        {
          title: 'Accounts',
          paragraphs: [
            'You must provide accurate information and keep your credentials secure.',
            'You are responsible for activity under your account and for ensuring authorized use by your team members.',
          ],
        },
        {
          title: 'Store owners and customer data',
          paragraphs: [
            'If you register a store, you are responsible for the customer data you collect through your storefront and for providing your own privacy notice to your customers where required.',
            'You must use Salovare lawfully and obtain any consents required for communications and marketing to your customers.',
          ],
        },
        {
          title: 'Payments',
          paragraphs: [
            'Online payments may be processed through Stripe. Additional Stripe terms apply when you connect a Stripe account or pay through the platform.',
            'Subscription and billing terms for store plans are described during registration and in the product.',
          ],
        },
        {
          title: 'Bookings and payments with stores',
          paragraphs: [
            'When you book with a store through Salovare, your contract for the service is with that store. The store is the seller and the merchant that charges your card. Salovare provides the booking and payment technology and is not a party to that contract.',
            'Before you pay online, you are shown what is charged now, what is left to pay at the store, and the cancellation terms that apply. You accept those terms by ticking the box before paying.',
            'If you cancel at least 2 hours before the appointment, you are refunded automatically, except for any deposit the store asked for, or otherwise the payment processing fee the store was charged, up to the amount shown before you paid.',
            'Cancellations less than 2 hours before the appointment, and not attending, are not refunded. If the store cancels, you are refunded in full.',
            'Refunds go back to the original payment method and usually take 5 to 10 business days to arrive. If something about a charge is wrong, please contact the store first; most problems are resolved faster that way than through a bank dispute.',
          ],
        },
        {
          title: 'SMS usage',
          paragraphs: [
            'Optional SMS notifications are billed at EUR 0.05 per message, aligned to your store subscription billing date.',
            'SMS spending is capped at EUR 30.00 per billing period. When the cap is reached, Stripe may issue an early invoice; SMS sending resumes after payment or at the next billing period.',
            'If you cancel your subscription, SMS already sent before the effective cancellation date remains billable on your final invoice together with subscription charges for that period.',
          ],
        },
        {
          title: 'Acceptable use',
          paragraphs: [
            'You may not misuse the platform, attempt unauthorized access, interfere with other users, or upload unlawful content.',
            'We may suspend or terminate access for violations or risk to the service.',
          ],
        },
        {
          title: 'Availability and liability',
          paragraphs: [
            'Salovare is provided on an "as is" and "as available" basis to the extent permitted by law.',
            'Nothing in these Terms limits rights that cannot be excluded under applicable consumer protection law.',
          ],
        },
        {
          title: 'Contact',
          paragraphs: [
            'Questions about these Terms: {{privacyEmail}}.',
          ],
        },
      ],
    },
  },
};

export const legalTranslationsPt = {
  legal: {
    common: {
      backToHome: 'Voltar ao início',
      lastUpdated: 'Última atualização: setembro de 2026',
    },
    footer: {
      privacy: 'Política de Privacidade',
      cookies: 'Política de Cookies',
      terms: 'Termos de Serviço',
      cookieSettings: 'Definições de cookies',
    },
    consent: {
      bannerTitle: 'Valorizamos a sua privacidade',
      bannerDescription: 'A Salovare usa armazenamento necessário para manter a aplicação segura e funcional. Com a sua autorização, também guardamos preferências, carregamos início de sessão de terceiros e medimos o desempenho da publicidade.',
      acceptAll: 'Aceitar tudo',
      rejectNonEssential: 'Rejeitar não essenciais',
      customize: 'Personalizar',
      preferencesTitle: 'Preferências de cookies',
      preferencesDescription: 'Escolha que armazenamento e integrações opcionais permite. O armazenamento necessário está sempre ativo porque o serviço não funciona sem ele.',
      necessaryTitle: 'Necessários',
      necessaryDescription: 'Necessários para autenticação, segurança e funcionalidades principais de marcações.',
      functionalTitle: 'Funcionais',
      functionalDescription: 'Memoriza idioma, rascunhos de formulários e avisos dispensados para melhorar a experiência.',
      thirdPartyTitle: 'Terceiros',
      thirdPartyDescription: 'Carrega o Google Sign-In quando escolhe autenticar com Google.',
      advertisingTitle: 'Publicidade',
      advertisingDescription: 'Permite ao Google Ads medir visitas e conversões dos nossos anúncios, com cookies de publicidade da Google.',
      savePreferences: 'Guardar preferências',
      alwaysOn: 'Sempre ativo',
      googleDisabled: 'O Google Sign-In está desativado até permitir cookies de terceiros nas definições de cookies.',
      openCookieSettings: 'Abrir definições de cookies',
    },
    registration: {
      acceptLabel: 'Concordo com os Termos de Serviço e a Política de Privacidade',
      acceptLabelRich: 'Concordo com os <termsLink>Termos de Serviço</termsLink> e a <privacyLink>Política de Privacidade</privacyLink>.',
      termsRequired: 'Tem de aceitar os Termos de Serviço e a Política de Privacidade para continuar.',
    },
    privacy: {
      title: 'Política de Privacidade',
      seoDescription: 'Como a Salovare trata dados pessoais no software para barbearias e salões, incluindo proprietários, equipas e clientes.',
      intro: 'Esta Política de Privacidade explica como a {{productName}} trata dados pessoais quando utiliza a nossa plataforma. A {{productName}} é operada por {{controllerName}} (NIF {{nif}}), com sede em {{country}}.',
      contact: 'Contacto de privacidade: {{privacyEmail}}',
      sections: [
        {
          title: 'Quem é responsável pelos seus dados',
          paragraphs: [
            'Responsável pelo tratamento: {{controllerName}} (NIF {{nif}}), {{country}}.',
            'Contacto para pedidos de privacidade: {{privacyEmail}}.',
            'A Salovare fornece software para barbearias e salões. Os proprietários das lojas podem ser responsáveis separados pelos dados dos seus clientes recolhidos através da respetiva loja.',
          ],
        },
        {
          title: 'Dados que tratamos',
          paragraphs: [
            'Dados de conta: nome, email, telefone, credenciais de autenticação, função e preferências.',
            'Dados da loja e equipa: detalhes do negócio, serviços, horários, membros da equipa e definições.',
            'Dados de marcações: agendamentos, serviços selecionados, informações de assinaturas e preferências de comunicação.',
            'Dados de pagamento: estado de pagamento, referências de faturação e identificadores Stripe. Os dados do cartão são tratados pela Stripe, não armazenados pela Salovare.',
            'Dados técnicos: endereço IP, tipo de browser, informação do dispositivo e registos de segurança.',
            'Dados de medição publicitária: identificadores de cliques em anúncios e eventos de conversão, tratados pelo Google Ads apenas se consentir cookies de publicidade.',
          ],
        },
        {
          title: 'Finalidades do tratamento',
          paragraphs: [
            'Prestar e proteger a plataforma Salovare (contrato e interesse legítimo).',
            'Gerir marcações, assinaturas e operações da loja solicitadas por utilizadores e equipa.',
            'Processar pagamentos através da Stripe quando os pagamentos online estão ativos.',
            'Autenticar utilizadores, incluindo Google Sign-In opcional quando o escolhe.',
            'Medir o desempenho da publicidade com o Google Ads quando consente cookies de publicidade.',
            'Cumprir obrigações legais e responder a pedidos legítimos.',
          ],
        },
        {
          title: 'Fundamentos jurídicos',
          paragraphs: [
            'Contrato: prestação do serviço contratado.',
            'Interesse legítimo: segurança da plataforma, prevenção de abuso e fiabilidade.',
            'Consentimento: armazenamento funcional opcional, início de sessão de terceiros e medição de publicidade quando exigido.',
            'Obrigação legal: requisitos fiscais, contabilísticos ou regulatórios aplicáveis.',
          ],
        },
        {
          title: 'Partilha e subcontratantes',
          paragraphs: [
            'Utilizamos fornecedores de confiança para operar a Salovare, incluindo alojamento, email, Stripe para pagamentos, Google para autenticação opcional e Google Ads para medição de publicidade quando consente.',
            'Os dados dos clientes da loja podem ser visíveis ao pessoal autorizado dessa loja. Os proprietários são responsáveis pelas suas próprias informações de privacidade aos clientes.',
            'Não vendemos dados pessoais.',
          ],
        },
        {
          title: 'Transferências internacionais',
          paragraphs: [
            'Alguns subcontratantes podem tratar dados fora do Espaço Económico Europeu. Nesses casos, recorremos a garantias adequadas, como Cláusulas Contratuais-Tipo ou mecanismos equivalentes.',
          ],
        },
        {
          title: 'Conservação',
          paragraphs: [
            'Conservamos dados pessoais apenas pelo tempo necessário para prestar o serviço, cumprir obrigações legais, resolver litígios e fazer cumprir acordos.',
            'Os prazos podem variar consoante o tipo de dados e a atividade da loja. Contacte-nos para detalhes sobre uma categoria específica.',
          ],
        },
        {
          title: 'Os seus direitos',
          paragraphs: [
            'Se estiver no EEE ou Reino Unido, pode ter direito de acesso, retificação, apagamento, limitação, oposição e portabilidade, quando aplicável.',
            'Pode retirar o consentimento a qualquer momento para tratamentos baseados em consentimento, sem afetar tratamentos anteriores lícitos.',
            'Pode apresentar reclamação à autoridade de controlo. Em Portugal, a CNPD.',
            'Para exercer os seus direitos, envie email para {{privacyEmail}}.',
          ],
        },
        {
          title: 'Segurança',
          paragraphs: [
            'Aplicamos medidas técnicas e organizativas para proteger dados pessoais, incluindo controlos de acesso, transporte encriptado e monitorização de abuso.',
          ],
        },
        {
          title: 'Alterações',
          paragraphs: [
            'Podemos atualizar esta política quando as nossas práticas mudarem. Atualizações relevantes serão refletidas nesta página com nova data.',
          ],
        },
      ],
    },
    cookies: {
      title: 'Política de Cookies',
      seoDescription: 'Como a Salovare usa cookies e armazenamento no browser no software para barbearias e salões, incluindo cookies necessários, funcionais, de início de sessão e de publicidade opcional.',
      intro: 'Esta Política de Cookies explica como a {{productName}}, operada por {{controllerName}}, utiliza cookies e armazenamento similar no browser.',
      sections: [
        {
          title: 'O que são cookies e tecnologias similares',
          paragraphs: [
            'Cookies são pequenos ficheiros de texto guardados no seu dispositivo. Também usamos local storage para algumas preferências e dados de sessão.',
          ],
        },
        {
          title: 'Armazenamento necessário',
          paragraphs: [
            'Tokens de autenticação e identificadores de sessão necessários para mantê-lo autenticado com segurança.',
            'Cookies de segurança usados durante fluxos de autenticação, incluindo cookies de refresh token definidos pela nossa API.',
            'São necessários para prestar o serviço e não podem ser desativados no centro de preferências.',
          ],
        },
        {
          title: 'Armazenamento funcional (opcional)',
          paragraphs: [
            'preferred-language: memoriza o idioma escolhido.',
            'Rascunhos de registo/atualização: gravações locais temporárias para não perder progresso.',
            'billing-reminder-dismissed:*: memoriza avisos de faturação dispensados por proprietários de loja.',
          ],
        },
        {
          title: 'Serviços de terceiros (opcional)',
          paragraphs: [
            'Google Sign-In (accounts.google.com): carregado apenas se permitir cookies de terceiros. A Google pode definir os seus próprios cookies.',
            'Google Ads (googletagmanager.com / googleadservices.com): carregado para medir visitas e conversões de anúncios apenas se permitir cookies de publicidade. A Google pode definir cookies de publicidade.',
            'Stripe: em páginas alojadas pela Stripe para pagamentos ou onboarding, a Stripe pode usar cookies nos domínios stripe.com.',
          ],
        },
        {
          title: 'Gerir as suas escolhas',
          paragraphs: [
            'Use o banner de cookies ou a ligação "Definições de cookies" no rodapé para atualizar preferências a qualquer momento.',
            'Também pode controlar cookies nas definições do browser. Bloquear armazenamento necessário pode impedir o funcionamento da aplicação.',
          ],
        },
        {
          title: 'Contacto',
          paragraphs: [
            'Questões sobre esta política: {{privacyEmail}}.',
          ],
        },
      ],
    },
    terms: {
      title: 'Termos de Serviço',
      seoDescription: 'Termos de utilização do software Salovare para barbearias e salões, incluindo subscrições, testes grátis, lojas e uso aceitável.',
      intro: 'Estes Termos regulam a utilização da {{productName}}, operada por {{controllerName}}. Ao criar conta ou usar a Salovare, concorda com estes Termos.',
      sections: [
        {
          title: 'O serviço',
          paragraphs: [
            'A Salovare fornece ferramentas online para marcações, gestão de loja, equipa, assinaturas e funcionalidades relacionadas para barbearias e salões.',
            'Podemos atualizar, suspender ou descontinuar funcionalidades com aviso razoável quando possível.',
          ],
        },
        {
          title: 'Contas',
          paragraphs: [
            'Deve fornecer informação exata e manter as credenciais seguras.',
            'É responsável pela atividade na sua conta e pelo uso autorizado por membros da equipa.',
          ],
        },
        {
          title: 'Proprietários de loja e dados de clientes',
          paragraphs: [
            'Se regista uma loja, é responsável pelos dados dos clientes recolhidos na sua loja e por fornecer aviso de privacidade próprio quando exigido.',
            'Deve usar a Salovare legalmente e obter consentimentos necessários para comunicações e marketing aos seus clientes.',
          ],
        },
        {
          title: 'Pagamentos',
          paragraphs: [
            'Pagamentos online podem ser processados pela Stripe. Termos adicionais da Stripe aplicam-se ao conectar conta Stripe ou pagar na plataforma.',
            'Condições de subscrição e faturação para planos de loja são descritas durante o registo e no produto.',
          ],
        },
        {
          title: 'Marcações e pagamentos com estabelecimentos',
          paragraphs: [
            'Quando marca com um estabelecimento através da Salovare, o contrato do serviço é celebrado com esse estabelecimento. O estabelecimento é o vendedor e o comerciante que cobra o seu cartão. A Salovare fornece a tecnologia de marcação e pagamento e não é parte desse contrato.',
            'Antes de pagar online, é-lhe mostrado o valor cobrado agora, o valor que fica a pagar no estabelecimento e as condições de cancelamento aplicáveis. Aceita essas condições ao assinalar a caixa antes de pagar.',
            'Se cancelar com pelo menos 2 horas de antecedência, é reembolsado automaticamente, exceto de qualquer sinal pedido pelo estabelecimento ou, caso contrário, da taxa de processamento do pagamento cobrada ao estabelecimento, até ao valor indicado antes de pagar.',
            'Os cancelamentos com menos de 2 horas de antecedência e as faltas não são reembolsados. Se o estabelecimento cancelar, é reembolsado na totalidade.',
            'Os reembolsos são feitos para o método de pagamento original e demoram normalmente 5 a 10 dias úteis. Se algo num pagamento estiver errado, contacte primeiro o estabelecimento; a maioria das situações resolve-se mais depressa assim do que através de uma contestação junto do banco.',
          ],
        },
        {
          title: 'Utilização de SMS',
          paragraphs: [
            'Notificações SMS opcionais são faturadas a EUR 0,05 por mensagem, alinhadas à data de faturação da subscrição da loja.',
            'O gasto com SMS é limitado a EUR 30,00 por período de faturação. Ao atingir o limite, a Stripe pode emitir uma fatura antecipada; o envio de SMS retoma após pagamento ou no período seguinte.',
            'Se cancelar a subscrição, os SMS já enviados antes da data efetiva de cancelamento permanecem faturáveis na fatura final juntamente com os encargos de subscrição desse período.',
          ],
        },
        {
          title: 'Uso aceitável',
          paragraphs: [
            'Não pode abusar da plataforma, tentar acesso não autorizado, interferir com outros utilizadores ou carregar conteúdo ilegal.',
            'Podemos suspender ou terminar acesso por violações ou risco para o serviço.',
          ],
        },
        {
          title: 'Disponibilidade e responsabilidade',
          paragraphs: [
            'A Salovare é fornecida "tal como está" e "conforme disponível", na medida permitida por lei.',
            'Nada nestes Termos limita direitos que não possam ser excluídos ao abrigo da lei de proteção do consumidor aplicável.',
          ],
        },
        {
          title: 'Contacto',
          paragraphs: [
            'Questões sobre estes Termos: {{privacyEmail}}.',
          ],
        },
      ],
    },
  },
};

export const legalTranslationsEs = {
  legal: {
    common: {
      backToHome: 'Volver al inicio',
      lastUpdated: 'Última actualización: septiembre de 2026',
    },
    footer: {
      privacy: 'Política de Privacidad',
      cookies: 'Política de Cookies',
      terms: 'Términos de Servicio',
      cookieSettings: 'Configuración de cookies',
    },
    consent: {
      bannerTitle: 'Valoramos su privacidad',
      bannerDescription: 'Salovare usa almacenamiento necesario para mantener la app segura y operativa. Con su permiso, también guardamos preferencias, cargamos inicio de sesión de terceros y medimos el rendimiento de la publicidad.',
      acceptAll: 'Aceptar todo',
      rejectNonEssential: 'Rechazar no esenciales',
      customize: 'Personalizar',
      preferencesTitle: 'Preferencias de cookies',
      preferencesDescription: 'Elija qué almacenamiento e integraciones opcionales permite. El almacenamiento necesario siempre está activo porque el servicio no puede funcionar sin él.',
      necessaryTitle: 'Necesarias',
      necessaryDescription: 'Requeridas para autenticación, seguridad y funciones principales de reservas.',
      functionalTitle: 'Funcionales',
      functionalDescription: 'Recuerda idioma, borradores de formularios y avisos descartados para mejorar su experiencia.',
      thirdPartyTitle: 'Terceros',
      thirdPartyDescription: 'Carga Google Sign-In cuando elige autenticarse con Google.',
      advertisingTitle: 'Publicidad',
      advertisingDescription: 'Permite a Google Ads medir visitas y conversiones de nuestros anuncios, con cookies publicitarias de Google.',
      savePreferences: 'Guardar preferencias',
      alwaysOn: 'Siempre activo',
      googleDisabled: 'Google Sign-In está desactivado hasta que permita cookies de terceros en la configuración de cookies.',
      openCookieSettings: 'Abrir configuración de cookies',
    },
    registration: {
      acceptLabel: 'Acepto los Términos de Servicio y la Política de Privacidad',
      acceptLabelRich: 'Acepto los <termsLink>Términos de Servicio</termsLink> y la <privacyLink>Política de Privacidad</privacyLink>.',
      termsRequired: 'Debe aceptar los Términos de Servicio y la Política de Privacidad para continuar.',
    },
    privacy: {
      title: 'Política de Privacidad',
      seoDescription: 'Cómo Salovare trata los datos personales en el software para barberías y salones, incluyendo propietarios, equipos y clientes.',
      intro: 'Esta Política de Privacidad explica cómo {{productName}} trata datos personales cuando utiliza nuestra plataforma. {{productName}} es operada por {{controllerName}} (NIF {{nif}}), con base en {{country}}.',
      contact: 'Contacto de privacidad: {{privacyEmail}}',
      sections: [
        {
          title: 'Quién es responsable de sus datos',
          paragraphs: [
            'Responsable del tratamiento: {{controllerName}} (NIF {{nif}}), {{country}}.',
            'Contacto para solicitudes de privacidad: {{privacyEmail}}.',
            'Salovare proporciona software para barberías y salones. Los propietarios de tiendas pueden ser responsables separados de los datos de sus clientes recopilados en su tienda.',
          ],
        },
        {
          title: 'Datos que tratamos',
          paragraphs: [
            'Datos de cuenta: nombre, email, teléfono, credenciales, rol y preferencias.',
            'Datos de tienda y equipo: detalles del negocio, servicios, horarios, miembros del equipo y configuración.',
            'Datos de reservas: reservas, servicios seleccionados, membresías y preferencias de comunicación.',
            'Datos de pago: estado de pago, referencias de facturación e identificadores Stripe. Los datos de tarjeta los procesa Stripe, no Salovare.',
            'Datos técnicos: IP, tipo de navegador, dispositivo y registros de seguridad.',
            'Datos de medición publicitaria: identificadores de clics en anuncios y eventos de conversión, tratados por Google Ads solo si consiente cookies de publicidad.',
          ],
        },
        {
          title: 'Finalidades',
          paragraphs: [
            'Prestar y proteger la plataforma Salovare (contrato e interés legítimo).',
            'Gestionar reservas, membresías y operaciones de la tienda solicitadas por usuarios y personal.',
            'Procesar pagos mediante Stripe cuando los pagos online están activos.',
            'Autenticar usuarios, incluido Google Sign-In opcional cuando lo elige.',
            'Medir el rendimiento publicitario con Google Ads cuando consiente cookies de publicidad.',
            'Cumplir obligaciones legales y responder a solicitudes legítimas.',
          ],
        },
        {
          title: 'Bases legales',
          paragraphs: [
            'Contrato: prestación del servicio contratado.',
            'Interés legítimo: seguridad, prevención de abuso y fiabilidad.',
            'Consentimiento: almacenamiento funcional opcional, inicio de sesión de terceros y medición publicitaria cuando se requiera.',
            'Obligación legal: requisitos fiscales, contables o regulatorios aplicables.',
          ],
        },
        {
          title: 'Compartición y subencargados',
          paragraphs: [
            'Usamos proveedores de confianza para operar Salovare, incluidos hosting, email, Stripe para pagos, Google para autenticación opcional y Google Ads para medición publicitaria cuando consiente.',
            'Los datos de clientes de la tienda pueden ser visibles al personal autorizado. Los propietarios son responsables de su propio aviso de privacidad.',
            'No vendemos datos personales.',
          ],
        },
        {
          title: 'Transferencias internacionales',
          paragraphs: [
            'Algunos subencargados pueden tratar datos fuera del EEE. En esos casos, usamos garantías adecuadas como Cláusulas Contractuales Tipo o mecanismos equivalentes.',
          ],
        },
        {
          title: 'Conservación',
          paragraphs: [
            'Conservamos datos personales solo el tiempo necesario para prestar el servicio, cumplir obligaciones legales, resolver disputas y hacer cumplir acuerdos.',
            'Los plazos pueden variar según el tipo de datos y la actividad de la tienda. Contáctenos para detalles sobre una categoría específica.',
          ],
        },
        {
          title: 'Sus derechos',
          paragraphs: [
            'Si está en el EEE o Reino Unido, puede tener derecho de acceso, rectificación, supresión, limitación, oposición y portabilidad cuando corresponda.',
            'Puede retirar el consentimiento en cualquier momento para tratamientos basados en consentimiento, sin afectar tratamientos previos lícitos.',
            'Puede presentar reclamación ante su autoridad de control. En Portugal, la CNPD.',
            'Para ejercer sus derechos, escriba a {{privacyEmail}}.',
          ],
        },
        {
          title: 'Seguridad',
          paragraphs: [
            'Aplicamos medidas técnicas y organizativas para proteger datos personales, incluidos controles de acceso, transporte cifrado y monitorización de abuso.',
          ],
        },
        {
          title: 'Cambios',
          paragraphs: [
            'Podemos actualizar esta política cuando cambien nuestras prácticas. Las actualizaciones relevantes se reflejarán en esta página con nueva fecha.',
          ],
        },
      ],
    },
    cookies: {
      title: 'Política de Cookies',
      seoDescription: 'Cómo Salovare usa cookies y almacenamiento del navegador en el software para barberías y salones, incluidas las cookies necesarias, funcionales, de inicio de sesión y de publicidad opcional.',
      intro: 'Esta Política de Cookies explica cómo {{productName}}, operada por {{controllerName}}, usa cookies y almacenamiento similar en el navegador.',
      sections: [
        {
          title: 'Qué son cookies y tecnologías similares',
          paragraphs: [
            'Las cookies son pequeños archivos de texto guardados en su dispositivo. También usamos local storage para algunas preferencias y datos de sesión.',
          ],
        },
        {
          title: 'Almacenamiento necesario',
          paragraphs: [
            'Tokens de autenticación e identificadores de sesión necesarios para mantenerle conectado de forma segura.',
            'Cookies de seguridad usadas en flujos de autenticación, incluidas cookies de refresh token definidas por nuestra API.',
            'Son necesarias para prestar el servicio y no pueden desactivarse en el centro de preferencias.',
          ],
        },
        {
          title: 'Almacenamiento funcional (opcional)',
          paragraphs: [
            'preferred-language: recuerda su idioma.',
            'Borradores de registro/actualización: guardados locales temporales para no perder progreso.',
            'billing-reminder-dismissed:*: recuerda avisos de facturación descartados por propietarios.',
          ],
        },
        {
          title: 'Servicios de terceros (opcional)',
          paragraphs: [
            'Google Sign-In (accounts.google.com): se carga solo si permite cookies de terceros. Google puede establecer sus propias cookies.',
            'Google Ads (googletagmanager.com / googleadservices.com): se carga para medir visitas y conversiones de anuncios solo si permite cookies de publicidad. Google puede establecer cookies publicitarias.',
            'Stripe: en páginas alojadas por Stripe para pagos u onboarding, Stripe puede usar cookies en dominios stripe.com.',
          ],
        },
        {
          title: 'Gestionar sus elecciones',
          paragraphs: [
            'Use el banner de cookies o el enlace "Configuración de cookies" en el pie para actualizar preferencias en cualquier momento.',
            'También puede controlar cookies en la configuración del navegador. Bloquear almacenamiento necesario puede impedir que la app funcione correctamente.',
          ],
        },
        {
          title: 'Contacto',
          paragraphs: [
            'Preguntas sobre esta política: {{privacyEmail}}.',
          ],
        },
      ],
    },
    terms: {
      title: 'Términos de Servicio',
      seoDescription: 'Términos de uso del software Salovare para barberías y salones, incluidas suscripciones, pruebas gratis, locales y uso aceptable.',
      intro: 'Estos Términos regulan el uso de {{productName}}, operada por {{controllerName}}. Al crear una cuenta o usar Salovare, acepta estos Términos.',
      sections: [
        {
          title: 'El servicio',
          paragraphs: [
            'Salovare proporciona herramientas online para reservas, gestión de tienda, equipo, membresías y funciones relacionadas para barberías y salones.',
            'Podemos actualizar, suspender o discontinuar funciones con aviso razonable cuando sea posible.',
          ],
        },
        {
          title: 'Cuentas',
          paragraphs: [
            'Debe proporcionar información exacta y mantener sus credenciales seguras.',
            'Es responsable de la actividad en su cuenta y del uso autorizado por miembros del equipo.',
          ],
        },
        {
          title: 'Propietarios de tienda y datos de clientes',
          paragraphs: [
            'Si registra una tienda, es responsable de los datos de clientes recopilados en su tienda y de proporcionar su propio aviso de privacidad cuando se requiera.',
            'Debe usar Salovare legalmente y obtener consentimientos necesarios para comunicaciones y marketing a sus clientes.',
          ],
        },
        {
          title: 'Pagos',
          paragraphs: [
            'Los pagos online pueden procesarse mediante Stripe. Se aplican términos adicionales de Stripe al conectar una cuenta o pagar en la plataforma.',
            'Las condiciones de suscripción y facturación se describen durante el registro y en el producto.',
          ],
        },
        {
          title: 'Reservas y pagos con establecimientos',
          paragraphs: [
            'Cuando reservas con un establecimiento a través de Salovare, el contrato del servicio es con ese establecimiento. El establecimiento es el vendedor y el comercio que realiza el cargo en tu tarjeta. Salovare proporciona la tecnología de reservas y pagos y no es parte de ese contrato.',
            'Antes de pagar online, se te muestra lo que se cobra ahora, lo que queda por pagar en el establecimiento y las condiciones de cancelación aplicables. Aceptas esas condiciones al marcar la casilla antes de pagar.',
            'Si cancelas con al menos 2 horas de antelación, se te reembolsa automáticamente, salvo la señal que pidiera el establecimiento o, en su defecto, la comisión de procesamiento del pago cobrada al establecimiento, hasta el importe indicado antes de pagar.',
            'Las cancelaciones con menos de 2 horas de antelación y las ausencias no se reembolsan. Si el establecimiento cancela, se te reembolsa el total.',
            'Los reembolsos se hacen al método de pago original y suelen tardar de 5 a 10 días hábiles. Si algo en un cargo no es correcto, contacta primero con el establecimiento; la mayoría de los problemas se resuelven antes así que mediante una reclamación al banco.',
          ],
        },
        {
          title: 'Uso de SMS',
          paragraphs: [
            'Las notificaciones SMS opcionales se facturan a EUR 0,05 por mensaje, alineadas a la fecha de facturación de la suscripción de la tienda.',
            'El gasto en SMS está limitado a EUR 30,00 por período de facturación. Al alcanzar el límite, Stripe puede emitir una factura anticipada; el envío de SMS se reanuda tras el pago o en el período siguiente.',
            'Si cancelas la suscripción, los SMS ya enviados antes de la fecha efectiva de cancelación siguen facturables en la factura final junto con los cargos de suscripción de ese período.',
          ],
        },
        {
          title: 'Uso aceptable',
          paragraphs: [
            'No puede abusar de la plataforma, intentar acceso no autorizado, interferir con otros usuarios ni subir contenido ilegal.',
            'Podemos suspender o terminar el acceso por violaciones o riesgo para el servicio.',
          ],
        },
        {
          title: 'Disponibilidad y responsabilidad',
          paragraphs: [
            'Salovare se proporciona "tal cual" y "según disponibilidad" en la medida permitida por la ley.',
            'Nada en estos Términos limita derechos que no puedan excluirse según la ley de protección al consumidor aplicable.',
          ],
        },
        {
          title: 'Contacto',
          paragraphs: [
            'Preguntas sobre estos Términos: {{privacyEmail}}.',
          ],
        },
      ],
    },
  },
};
